/** ID Austria OIDC — Client (Login + Einstellungen). Kein Token-Tausch im Browser. */

import { portalAuthHeaders } from './webauthnClient';

export const ID_AUSTRIA_API = '/api/webauthn/id-austria';

export type IdAustriaStatus = {
  configured: boolean;
  issuer: string | null;
  redirect_uri: string | null;
  reason?: string | null;
};

export type IdAustriaLinkStatus = {
  linked: boolean;
  linked_at?: string | null;
  collection_ready?: boolean;
};

async function readApiJson(res: Response, fallback: string): Promise<Record<string, unknown>> {
  const text = await res.text();
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    if (text.trimStart().startsWith('<')) {
      throw new Error(
        'ID-Austria-Dienst nicht erreichbar (HTML statt JSON). Bitte später erneut versuchen.',
      );
    }
    throw new Error(fallback);
  }
}

export async function fetchIdAustriaStatus(): Promise<IdAustriaStatus> {
  const r = await fetch(`${ID_AUSTRIA_API}/status`);
  const body = await readApiJson(r, 'ID-Austria-Status nicht lesbar');
  return {
    configured: body.configured === true,
    issuer: typeof body.issuer === 'string' ? body.issuer : null,
    redirect_uri: typeof body.redirect_uri === 'string' ? body.redirect_uri : null,
    reason: typeof body.reason === 'string' ? body.reason : null,
  };
}

export function idAustriaLoginStartUrl(): string {
  return `${ID_AUSTRIA_API}/start`;
}

export async function fetchIdAustriaLink(customerId: string): Promise<IdAustriaLinkStatus> {
  const r = await fetch(`${ID_AUSTRIA_API}/link?customerId=${encodeURIComponent(customerId)}`, {
    headers: portalAuthHeaders(),
  });
  const body = await readApiJson(r, 'ID-Austria-Verknüpfung nicht lesbar');
  if (!r.ok) throw new Error(String(body.error || 'ID-Austria-Verknüpfung nicht lesbar'));
  return {
    linked: body.linked === true,
    linked_at: typeof body.linked_at === 'string' ? body.linked_at : null,
    collection_ready: body.collection_ready !== false,
  };
}

export async function startIdAustriaLink(customerId: string): Promise<string> {
  const r = await fetch(`${ID_AUSTRIA_API}/link/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...portalAuthHeaders() },
    body: JSON.stringify({ customerId }),
  });
  const body = await readApiJson(r, 'ID Austria konnte nicht gestartet werden.');
  if (r.status === 503) {
    throw new Error(String(body.error || 'ID Austria ist auf diesem Server noch nicht eingerichtet.'));
  }
  if (!r.ok || typeof body.url !== 'string' || !body.url) {
    throw new Error(String(body.error || 'ID Austria konnte nicht gestartet werden.'));
  }
  return body.url;
}

export async function unlinkIdAustria(customerId: string): Promise<void> {
  const r = await fetch(`${ID_AUSTRIA_API}/link`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...portalAuthHeaders() },
    body: JSON.stringify({ customerId }),
  });
  const body = await readApiJson(r, 'Trennen fehlgeschlagen.');
  if (!r.ok) throw new Error(String(body.error || 'Trennen fehlgeschlagen.'));
}

export async function redeemIdAustriaTicket(ticket: string): Promise<{
  token: string;
  record: Record<string, unknown>;
}> {
  const r = await fetch(`${ID_AUSTRIA_API}/redeem`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ticket }),
  });
  const body = await readApiJson(r, 'ID-Austria-Ticket ungültig. Es wurde keine Sitzung erzeugt.');
  if (!r.ok) {
    throw new Error(String(body.error || 'ID-Austria-Ticket ungültig. Es wurde keine Sitzung erzeugt.'));
  }
  const token = typeof body.token === 'string' ? body.token : '';
  const record =
    body.record && typeof body.record === 'object' ? (body.record as Record<string, unknown>) : null;
  if (!token || !record?.id) {
    throw new Error('ID-Austria-Antwort unvollständig. Es wurde keine Sitzung erzeugt.');
  }
  return { token, record };
}

export function messageForIdaError(code: string | null): string {
  switch (code) {
    case 'not_linked':
      return 'ID Austria ist mit keinem Portal-Konto verknüpft. Bitte zuerst mit Passwort anmelden und unter Einstellungen verbinden.';
    case 'not_configured':
      return 'ID Austria ist auf diesem Server noch nicht eingerichtet.';
    case 'token_failed':
      return 'Die Antwort von ID Austria konnte nicht geprüft werden. Es wurde keine Sitzung erzeugt.';
    case 'aborted':
      return 'Anmeldung mit ID Austria abgebrochen.';
    case 'link_failed':
      return 'ID Austria konnte nicht mit diesem Konto verknüpft werden.';
    case 'link_conflict':
      return 'Diese ID Austria ist bereits mit einem anderen Konto verbunden.';
    default:
      return code ? 'ID-Austria-Anmeldung fehlgeschlagen. Es wurde keine Sitzung erzeugt.' : '';
  }
}
